import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Megaphone, Store } from "lucide-react";
import { api } from "@/lib/api";
import { fmtPrice } from "@/lib/currency";
import { GuestWantForm } from "@/components/GuestWantForm";

export function CampaignWelcome({ code }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get(`/c/${encodeURIComponent(code)}/info`).then(({ data }) => setInfo(data)).catch(() => setInfo(null));
  }, [code]);

  if (!info) return null;
  const label = info.category_label || "products";
  const browseTo = info.category ? `/discover?category=${info.category}` : "/discover";

  return (
    <section className="relative border-b border-[#27272A] bg-[#0E0E10]" data-testid="campaign-welcome">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#007AFF]/40 bg-[#007AFF]/10 text-xs text-[#9CC8FF]" data-testid="campaign-welcome-source">
          <Megaphone className="h-3.5 w-3.5" /> Welcome from {info.source_name || "our community"}
        </div>
        <h2 className="mt-4 font-black tracking-tight text-3xl sm:text-4xl leading-tight">
          Looking for {label}? <span className="text-[#34C759]">{info.live_in_category.toLocaleString()} live listings</span> — browse free, no sign-up.
        </h2>
        <p className="mt-3 max-w-2xl text-neutral-400 text-sm sm:text-base">
          Buy with buyer protection until delivery, or tell sellers exactly what you want and get matched. Selling {label}? List unlimited products for one flat fee — founding sellers get the first month free.
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <Link to={browseTo} data-testid="campaign-welcome-browse" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-black text-sm font-semibold hover:bg-neutral-200 transition-colors">
            <Search className="h-4 w-4" /> Browse {label} <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to={`/register?role=buyer&cmp=${encodeURIComponent(code)}`} data-testid="campaign-welcome-want" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/20 text-sm font-semibold hover:bg-white/5 transition-colors">
            Open a buyer account
          </Link>
          <Link to={`/register?role=seller&cmp=${encodeURIComponent(code)}`} data-testid="campaign-welcome-sell" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-[#FF3B30]/50 bg-[#FF3B30]/10 text-sm font-semibold text-white hover:bg-[#FF3B30]/20 transition-colors">
            <Store className="h-4 w-4" /> Sell {label} here — first month free
          </Link>
        </div>

        <div className="mt-6 max-w-2xl">
          <GuestWantForm cmp={code} />
        </div>

        {info.products.length > 0 && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="campaign-welcome-products">
            {info.products.map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} className="group rounded-xl border border-[#27272A] bg-[#121212] overflow-hidden hover:border-white/30 transition-colors">
                <div className="aspect-square bg-[#1A1A1C] overflow-hidden">
                  {p.image_url && <img src={p.image_url} alt={p.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />}
                </div>
                <div className="p-2.5">
                  <div className="text-xs text-white line-clamp-2 leading-snug">{p.title}</div>
                  <div className="mt-1 font-mono text-sm font-bold text-[#34C759]">{fmtPrice(p.price, p.currency)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
